import { useEffect } from "react";

import { useRouter } from "next/router";

import { LoadingSpinner } from "@newsela/angelou";

import BaseLayout from "@/base";
import RootProviders from "@/base/RootProviders";
import ErrorMessage from "@/errors/ErrorMessage";
import NotLoggedInError from "@/errors/NotLoggedInError";
import { useGetUser } from "@/users/v3";
import useUserFeaturesQuery from "@/users/v3/useUserFeaturesQuery";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";

import BundlePage from "@/bundles/BundlePage";
import { BROWSE_PAGE_PRODUCT_IDS } from "../../browse/[slug]/[contentId]";

import type { NextPageContext } from "next";

const BundlePageRoute = () => {
  const { push, query } = useRouter();
  const contentId = query["contentId"] as string;

  const { data: user, isLoading: isLoadingUser, isError } = useGetUser();
  const { data: features, isLoading: isUserFeaturesLoading } =
    useUserFeaturesQuery();

  const hasSTEMLicense = features?.includes("has_stem_license");

  const [newBundlesExperience] = useGetUserExperimentSegment(
    "bundles_page_rebuild",
  );
  const isBundleExperimentActive = newBundlesExperience === "on";

  const canSeeNewBundlesExperience =
    hasSTEMLicense ||
    (isBundleExperimentActive && BROWSE_PAGE_PRODUCT_IDS.includes(contentId));

  useEffect(() => {
    if (features) {
      if (!canSeeNewBundlesExperience) {
        // Redirect to old subject page.
        push(`/content/${contentId}`);
      }
    }
  }, [features, canSeeNewBundlesExperience]);

  if (isUserFeaturesLoading || isLoadingUser) {
    return <BaseLayout mainContents={<LoadingSpinner />} />;
  }

  if (isError) {
    return (
      <BaseLayout
        mainContents={
          <ErrorMessage headline="Oh no! Something went wrong.">
            <p>Either you do not have access, or an error has occurred.</p>
          </ErrorMessage>
        }
      />
    );
  }

  if (!user?.is_authenticated) {
    return <BaseLayout mainContents={<NotLoggedInError />} />;
  }

  return <BaseLayout mainContents={<BundlePage />} />;
};

export const getServerSideProps = async (ctx: NextPageContext) => {
  const RootProvidersInitialProps = await RootProviders.__getInitialProps({
    ctx,
  });

  return {
    props: {
      [RootProviders.InitialPropsName]: RootProvidersInitialProps,
    },
  };
};

export default BundlePageRoute;
